import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Loader2, Save, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

function effectiveSharePct(agent, defaultPct) {
  const raw = agent?.commission_share_pct;
  if (raw === null || raw === undefined || raw === '') {
    return { pct: Number(defaultPct) || 0, usesDefault: true, excluded: false };
  }
  const n = Number(raw);
  if (Number.isNaN(n)) {
    return { pct: Number(defaultPct) || 0, usesDefault: true, excluded: false };
  }
  return { pct: n, usesDefault: false, excluded: n === 0 };
}

export default function AgentCommissionSharingRoster({ settings }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [toast, setToast] = useState(null);

  const defaultPct = Number(settings?.agent_commission_default_pct) || 0;
  const moduleOn = Boolean(settings?.commission_tds_enabled);

  const loadAgents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/agents`, {
        withCredentials: true,
        params: { limit: 500, page: 1 },
      });
      const list = Array.isArray(r.data) ? r.data : r.data?.agents || [];
      setAgents(list);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return agents.filter((a) => {
      const eff = effectiveSharePct(a, defaultPct);
      if (filter === 'receiving' && !(eff.pct > 0)) return false;
      if (filter === 'excluded' && !eff.excluded) return false;
      if (!q) return true;
      const hay = `${a.agency_name || ''} ${a.full_name || ''} ${a.email || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [agents, search, filter, defaultPct]);

  const handleSave = async (agentId) => {
    const val = drafts[agentId];
    const pct = parseFloat(val);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      setToast({ type: 'err', msg: 'Enter a share between 0 and 100' });
      return;
    }
    setSavingId(agentId);
    try {
      await axios.patch(
        `${API}/agents/${agentId}/commission`,
        { commission_share_pct: pct },
        { withCredentials: true },
      );
      setAgents((prev) =>
        prev.map((a) => (a.id === agentId ? { ...a, commission_share_pct: pct } : a)),
      );
      setToast({ type: 'ok', msg: 'Commission share updated' });
    } catch (e) {
      setToast({ type: 'err', msg: e?.response?.data?.detail || 'Save failed' });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="agent-commission-sharing-roster">
      {!moduleOn && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Commission &amp; TDS module is <strong>OFF</strong>. Enable it in{' '}
          <Link to="/admin/compliance/settings" className="underline font-semibold">
            Compliance Settings
          </Link>{' '}
          for sharing to apply at booking time.
        </div>
      )}

      <p className="text-sm text-gray-600">
        Set each agent&apos;s share of supplier gross commission (TF−NF). Agents at{' '}
        <strong>0%</strong> are excluded. Leave blank to use the global default ({defaultPct}%).
        Also editable in{' '}
        <Link to="/admin/agents" className="text-[#1B375B] underline font-semibold">
          Agent Management
        </Link>
        .
      </p>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            placeholder="Search agency, name, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm"
            data-testid="agent-sharing-search"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
          data-testid="agent-sharing-filter"
        >
          <option value="all">All agents</option>
          <option value="receiving">Receiving commission</option>
          <option value="excluded">Excluded (0%)</option>
        </select>
      </div>

      {toast && (
        <div
          className={`text-sm px-3 py-2 rounded-lg ${
            toast.type === 'ok' ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800'
          }`}
        >
          {toast.msg}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : error ? (
        <p className="text-sm text-rose-600">{error}</p>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-3 py-2 font-semibold text-gray-600">Agency</th>
                <th className="px-3 py-2 font-semibold text-gray-600">Agent</th>
                <th className="px-3 py-2 font-semibold text-gray-600">Status</th>
                <th className="px-3 py-2 font-semibold text-gray-600">Share %</th>
                <th className="px-3 py-2 font-semibold text-gray-600">Effective</th>
                <th className="px-3 py-2 font-semibold text-gray-600 w-24" />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-gray-500">
                    No agents match your filters.
                  </td>
                </tr>
              )}
              {filtered.map((a) => {
                const eff = effectiveSharePct(a, defaultPct);
                const draftVal =
                  drafts[a.id] !== undefined
                    ? drafts[a.id]
                    : a.commission_share_pct ?? '';
                return (
                  <tr key={a.id} className="border-t border-gray-100">
                    <td className="px-3 py-2 font-medium">{a.agency_name || '—'}</td>
                    <td className="px-3 py-2">{a.full_name || a.email || '—'}</td>
                    <td className="px-3 py-2 capitalize text-xs">{a.status || '—'}</td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        placeholder={`Default ${defaultPct}%`}
                        value={draftVal}
                        onChange={(e) =>
                          setDrafts((d) => ({ ...d, [a.id]: e.target.value }))
                        }
                        className="w-24 px-2 py-1 border border-gray-200 rounded text-sm"
                        data-testid={`agent-share-input-${a.id}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      {eff.excluded ? (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-800">
                          Excluded
                        </span>
                      ) : eff.usesDefault ? (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                          Default: {defaultPct}%
                        </span>
                      ) : (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-sky-100 text-sky-800">
                          Custom: {eff.pct}%
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => handleSave(a.id)}
                        disabled={savingId === a.id}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold text-[#1B375B] border border-[#659A9B] rounded hover:bg-[#659A9B]/10 disabled:opacity-50"
                        data-testid={`agent-share-save-${a.id}`}
                      >
                        {savingId === a.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Save className="w-3 h-3" />
                        )}
                        Save
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
